import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowBack } from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import styles from "./CognitiveCheckResults.module.css";
import cognitiveData from "../../data/cognitive.json";
import { getCognitiveTestSetId } from "../../utils/testId";
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, setDoc, arrayUnion } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { getUserId } from "../../utils/userId";

// --- 인터페이스 정의 ---
interface TestResult {
  testType: string;
  results: {
    totalScore?: number;
    finalSpanLength?: number;
    totalSuccess?: number;
    wordsEntered?: string;
    correctWords?: string;
    totalTime?: number;
    moves?: number;
    isSolved?: boolean;
    problemId?: number;
    minMoves?: number;
  };
}

interface FormattedRiskFactor {
  keyword: string;
  description: string;
}

interface FormattedTestScore {
  testType: string;
  score: number;
  threshold: number;
  totalCount: number;
  isRisk: boolean;
}

interface LocationState {
  sessionId?: string | null;
}

const CognitiveCheckResults: React.FC = () => {
  const navigate = useNavigate();
  const { t, ready } = useTranslation();
  const { testSetId: testSetIdFromParams, hospitalId } = useParams<{ testSetId: string; hospitalId?: string }>();
  const prefix = hospitalId ? `/${hospitalId}` : '';

  // --- State ---
  const [riskFactors, setRiskFactors] = useState<FormattedRiskFactor[]>([]);
  const [testScores, setTestScores] = useState<FormattedTestScore[]>([]);
  const [savingToFirebase, setSavingToFirebase] = useState(false);
  const [shareDocId, setShareDocId] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [isViewingOldReport, setIsViewingOldReport] = useState(false);

  // --- Refs ---
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  // [핵심] 무한 루프 방지용 강력한 자물쇠
  // 이 값이 true가 되면 어떤 일이 있어도 데이터 로딩을 다시 하지 않습니다.
  const hasFetchedData = useRef(false);

  // --- Helper Data & Functions ---
  const testMapper = {
    test1: "digit_span",
    test2: "random_cube",
    test3: "verbal_learning_test",
    test4: "tower_of_london",
  };

  const getTestScore = (result: TestResult): number => {
    const testType = testMapper[result.testType as keyof typeof testMapper];
    switch (testType) {
      case "digit_span":
        return result.results.totalScore || result.results.finalSpanLength || 0;
      case "random_cube":
        return result.results.totalScore || 0;
      case "tower_of_london":
        return result.results.totalScore || 0;
      case "verbal_learning_test":
        return result.results.totalScore || 0;
      default:
        return 0;
    }
  };

  // --- 1. 화면 초기화 (스크롤) ---
  useLayoutEffect(() => {
    // 1. 스크롤 강제 초기화
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // 2. 내부 컨텐츠 스크롤도 초기화
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, []);


  // --- 2. 메인 로직 (데이터 로드 & 저장 & QR 생성) ---
  // 모든 과정을 하나의 useEffect로 통합하여 의존성 충돌을 방지합니다.
  useEffect(() => {
    // 1. 준비 안 됐거나 이미 처리했으면 중단
    if (!ready || hasFetchedData.current) return;

    const init = async () => {
      // 자물쇠 잠금 (두 번 실행 방지)
      hasFetchedData.current = true;

      // 2. ID 결정 로직
      let targetId = testSetIdFromParams;
      let isOld = true;

      if (!targetId) {
        const idFromStorage = getCognitiveTestSetId();
        if (idFromStorage) {
          targetId = idFromStorage;
          isOld = false;
        }
      }

      if (!targetId) {
        setQrError("테스트 ID를 찾을 수 없습니다.");
        return;
      }

      // 상태 업데이트 (UI용)
      setIsViewingOldReport(isOld);
      console.log(`🚀 시작: ${isOld ? "과거 리포트 조회" : "새 결과 처리"}, ID: ${targetId}`);

      // 3. 내부 함수: 공유된 리포트 불러오기
      const loadShared = async (id: string) => {
        const q = query(collection(db, "sharedCognitiveResults"), where("testSetId", "==", id));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const data = snap.docs[0].data();
          console.log("✅ 이미 공유된 리포트 발견:", snap.docs[0].id);
          setRiskFactors(data.riskFactors || []);
          setTestScores(data.testScores || []);
          setShareDocId(snap.docs[0].id);
          return true; // 찾음
        }
        return false; // 못 찾음
      };

      // 4. 내부 함수: 결과 계산 및 저장
      const calculateAndSave = async (id: string) => {
        setSavingToFirebase(true);
        try {
          // (1) 사용자 응답 데이터 가져오기
          const q = query(collection(db, "userCognitiveResponse"), where("testSetId", "==", id));
          const snap = await getDocs(q);
          
          if (snap.empty) {
            setQrError("결과 데이터를 찾을 수 없습니다.");
            return;
          }

          const results = snap.docs.map(doc => doc.data() as TestResult);

          // (2) 점수 계산
          const computedScores = results.map(result => {
            const testTypeKey = testMapper[result.testType as keyof typeof testMapper];
            const config = (cognitiveData as any)[testTypeKey as keyof typeof cognitiveData];
            if (!config) return null;

            const score = getTestScore(result);
            const totalCount = config.totalCount || 0;
            const isRisk = score <= config.threshold;

            return {
              testType: result.testType,
              score,
              threshold: config.threshold,
              totalCount,
              isRisk
            };
          }).filter(Boolean) as FormattedTestScore[];

          // (3) 위험 요인 도출
          const computedRisks: FormattedRiskFactor[] = [];
          computedScores.forEach(score => {
            if (score.isRisk) {
              const tKey = testMapper[score.testType as keyof typeof testMapper];
              const conf = cognitiveData[tKey as keyof typeof cognitiveData];
              if (conf) {
                computedRisks.push({
                  keyword: conf.risk_keyword,
                  description: conf.risk_description
                });
              }
            }
          });

          setTestScores(computedScores);
          setRiskFactors(computedRisks);

          // (4) Firestore에 공유 문서 저장 (QR용)
          const userId = getUserId();
          if (userId) {
            const formattedRisks = computedRisks.map(r => ({
              keyword: t(r.keyword),
              description: t(r.description)
            }));
            const formattedScores = computedScores.map(s => ({
              ...s
            })); // 필요시 번역 추가

            const payload = {
              kind: "cognitive",
              createdAt: serverTimestamp(),
              userId,
              testSetId: id,
              riskFactors: formattedRisks,
              testScores: formattedScores,
              totalRiskCount: computedRisks.length
            };

            const docRef = await addDoc(collection(db, "sharedCognitiveResults"), payload);
            console.log("✨ 공유 문서 생성 완료:", docRef.id);
            setShareDocId(docRef.id);

            const sessionId = locationState?.sessionId;
            if (sessionId) {
              const reportRef = doc(db, "reports", sessionId);
              await setDoc(reportRef, {
                hospitalId: hospitalId || "default",
                patientId: userId,
                timestamp: serverTimestamp(),
                cognitiveReportId: docRef.id,
                checkTypes: arrayUnion("Cognitive")
              }, { merge: true });
            }
          }

        } catch (e) {
          console.error(e);
          setQrError("결과 처리 중 오류 발생");
        } finally {
          setSavingToFirebase(false);
        }
      };

      // 5. 실행 흐름 제어
      if (isOld) {
        // 옛날 거면 공유된 거 먼저 찾고, 없으면 다시 계산
        const found = await loadShared(targetId);
        if (!found) {
          console.log("⚠️ 공유 문서 없음, 재생성 시도...");
          await calculateAndSave(targetId);
        }
      } else {
        // 새 거면 바로 계산 및 저장
        await calculateAndSave(targetId);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]); // ready가 true가 되는 순간 딱 한 번만 실행


  // --- UI Event Handlers ---
  
  const handleBack = () => {
    navigate(-1);
  };

  const handleConfirm = () => {
    console.log("🔘 버튼 클릭됨!");

    try {
      if (!isViewingOldReport) {
        localStorage.removeItem("cognitiveAnswers");
        localStorage.removeItem("cognitiveTestSetId");
      }
    } catch (e) {
      console.error("Storage clear error:", e);
    }

    // 강제 이동
    navigate(`${prefix}/cognitive_check`);
  };

  // 스크롤바 숨김 처리
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const onScroll = () => {
      el.classList.remove(styles.scrollHidden);
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => el.classList.add(styles.scrollHidden), 400);
    };
    el.addEventListener('scroll', onScroll);
    el.classList.add(styles.scrollHidden);
    return () => {
      el.removeEventListener('scroll', onScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, []);

  // --- Render Helpers ---
  const publicOrigin = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
  const shareUrl = shareDocId ? `${publicOrigin}${prefix}/cognitive/report/${shareDocId}` : "";

  const getRiskLevelClass = (riskCount: number): string => {
    if (riskCount >= 3) return styles.riskHigh;
    if (riskCount >= 1) return styles.riskMedium;
    return styles.riskLow;
  }
  const riskClass = getRiskLevelClass(riskFactors.length);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('check_before_2')}</span>
      </div>

      <div className={styles.content}>
        <div className={styles.contentScroll} ref={contentRef}>
          <div className={styles.imageContainer}>
            <span className={styles.resultTitle}>{t("test_result_title")}</span>
          </div>

          <div className={styles.riskDisplayContainer}>
            <div className={`${styles.yesCountContainer} ${riskClass}`}>
              <span className={styles.yesCount}>{riskFactors.length}</span>
            </div>
            <span className={styles.riskTotalText}>
              / {Object.keys(testMapper).length}
            </span>
          </div>

          <div className={styles.resultDescription}>
            {t("test_result_description")}
          </div>

          {testScores.map((testScore, index) => {
            let testKey = testScore.testType;
            // 매핑 키 찾기
            if (!testMapper[testKey as keyof typeof testMapper]) {
              const foundKey = Object.keys(testMapper).find(
                (key) => testMapper[key as keyof typeof testMapper] === testKey
              );
              if (foundKey) testKey = foundKey;
            }

            const i18nKey = `test_type_${testKey}`;
            let testName: string = t(i18nKey);
            if (testName === i18nKey) testName = testKey; 

            let totalCount = testScore.totalCount;
            // Total Count fallback
            if (!totalCount) {
              const testConfigKey = testMapper[testKey as keyof typeof testMapper];
              const testConfig = (cognitiveData as any)[testConfigKey as keyof typeof cognitiveData];
              totalCount = testConfig?.totalCount || 0;
            }

            return (
              <div
                key={index}
                className={`${styles.riskFactorContainer} ${
                  testScore.isRisk ? styles.risk : ""
                }`}
              >
                <div className={styles.riskFactorKeyword}>
                  {testName}: {testScore.score} / {totalCount}
                </div>
                <div className={styles.riskFactorDescription}>
                  {testScore.isRisk ? t("risk") : t("normal")}
                </div>
              </div>
            );
          })}

          {riskFactors.map((riskFactor, index) => (
            <div key={index} className={styles.riskFactorContainer}>
              <div className={styles.riskFactorKeyword}>
                {t(riskFactor.keyword)}
              </div>
              <div className={styles.riskFactorDescription}>
                {t(riskFactor.description)}
              </div>
            </div>
          ))}

          <div className={styles.qrCodeContainer}>
            {shareUrl ? (
              <>
                <div className={styles.qrWrapper}>
                  <QRCodeCanvas
                    key={shareUrl}
                    value={shareUrl}
                    size={180}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                    includeMargin
                  />
                </div>
                <div className={styles.qrCodeDescription}>
                  {t("scan_qr_code")}
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    height: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0.7,
                    flexDirection: "column",
                  }}
                >
                  {savingToFirebase ? (t("generating_qr") || "QR 생성 중...") : "..."}
                </div>
                {qrError && (
                  <>
                    <div style={{ fontSize: 12, opacity: 0.8, color: "red" }}>
                      ({qrError})
                    </div>
                    <button
                      className={styles.copyLinkButton}
                      onClick={() => window.location.reload()}
                      style={{ marginTop: 4, background: "#888" }}
                    >
                      {t("retry") || "다시 시도"}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      <button
        type="button"
        className={styles.confirmButton}
        onClick={handleConfirm}
        style={{ zIndex: 99999, cursor: 'pointer' }} 
      >
        {t("confirm_button_text", "확인")}
      </button>
    </div>
  );
};

export default CognitiveCheckResults;